import { BellOff, BellRing, Download, MonitorSmartphone, Share, SquarePlus } from 'lucide-react'
import styles from './DeviceNotificationsContent.module.css'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { UseInstallPromptResult } from '@/hooks/useInstallPrompt'
import type { UsePushSubscriptionResult } from '@/hooks/usePushSubscription'
import { cn } from '@/lib/utils'

const STATUS_COPY: Record<string, { title: string; description: string }> = {
  loading: {
    title: 'Checking notifications…',
    description: 'Getting things ready.',
  },
  unsupported: {
    title: 'Not supported on this browser',
    description:
      'Web push notifications require a recent version of Chrome, Edge, Firefox, or Safari. Try a different browser.',
  },
  insecure: {
    title: 'HTTPS required',
    description: 'Notifications only work on a secure (HTTPS) connection.',
  },
  denied: {
    title: 'Notifications are blocked',
    description:
      'Reset this site’s notification permission in your browser settings, then reload this page to try again.',
  },
  idle: {
    title: 'Turn on notifications',
    description:
      'Get alerts on this device when you have new messages, updates, or tasks — even when the app is closed.',
  },
  subscribed: {
    title: 'Notifications are on',
    description: 'This device will receive push alerts from Doneverse.',
  },
}

interface DeviceNotificationsContentProps {
  push: UsePushSubscriptionResult
  install: UseInstallPromptResult
}

export function DeviceNotificationsContent({ push, install }: DeviceNotificationsContentProps) {
  const copy = STATUS_COPY[push.status] ?? STATUS_COPY.loading
  const isActionable = push.status === 'idle' || push.status === 'subscribed' || push.status === 'denied'
  const needsIosInstall = push.isIos && !push.isStandalone

  return (
    <div className={styles.container}>
      {needsIosInstall ? (
        <Card className={styles.card}>
          <CardContent className="p-0 flex flex-col gap-4">
            <div>
              <div className={styles.title}>Add Doneverse to your Home Screen</div>
              <div className={styles.description}>
                iOS only delivers push notifications to apps installed on the Home Screen. Install Doneverse,
                then open it from the new icon to turn on notifications.
              </div>
            </div>

            <ol className={styles.steps}>
              <li>
                In Safari, tap the <strong>Share</strong> button
                <Share className="inline h-4 w-4 ml-1 align-text-bottom" />
              </li>
              <li>
                Choose <strong>Add to Home Screen</strong>
                <SquarePlus className="inline h-4 w-4 ml-1 align-text-bottom" />
              </li>
              <li>Open Doneverse from the new icon and come back to this page.</li>
            </ol>
          </CardContent>
        </Card>
      ) : (
        <Card className={styles.card}>
          <CardContent className="p-0 flex flex-col gap-4">
            <div className={styles.statusRow}>
              <span
                className={cn(
                  styles.dot,
                  push.status === 'subscribed' && styles.dotActive,
                  push.status === 'denied' && styles.dotDenied,
                )}
              />
              <span>
                {push.status === 'subscribed'
                  ? 'Enabled on this device'
                  : push.status === 'denied'
                    ? 'Blocked'
                    : push.status === 'loading'
                      ? 'Loading…'
                      : 'Not enabled'}
              </span>
            </div>

            <div>
              <div className={styles.title}>{copy.title}</div>
              <div className={styles.description}>{copy.description}</div>
            </div>

            {push.error && <div className={styles.error}>{push.error}</div>}

            <div className={styles.actions}>
              {install.canInstall && (
                <Button variant="outline" onClick={() => void install.promptInstall()} disabled={push.isBusy}>
                  <Download className="h-4 w-4 mr-2" />
                  Install app
                </Button>
              )}

              {push.status === 'subscribed' ? (
                <>
                  <Button variant="outline" onClick={() => void push.unsubscribe()} disabled={push.isBusy}>
                    <BellOff className="h-4 w-4 mr-2" />
                    Disable notifications
                  </Button>
                  <Button variant="ghost" onClick={() => void push.sendLocalTest()} disabled={push.isBusy}>
                    Show test notification
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => void push.subscribe()}
                  disabled={!isActionable || push.isBusy || push.status === 'denied'}
                >
                  <BellRing className="h-4 w-4 mr-2" />
                  Enable notifications
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={styles.card}>
        <CardContent className="p-0 flex flex-col gap-3">
          <div className={styles.title} style={{ fontSize: 16 }}>
            <MonitorSmartphone className="inline h-4 w-4 mr-2" />
            Good to know
          </div>
          <ul className={styles.helpList}>
            <li>
              Each device has its own subscription — enable notifications on every computer and phone you use.
            </li>
            <li>
              If you previously blocked notifications, reset the permission in your browser’s site settings.
            </li>
            <li>On iOS, you must add Doneverse to your Home Screen first, then open it from there.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
